/**
 * This function will Capitalize the first letter of every word.
 * @param text string
 * @returns string
 * @example "this is a test" will transform to "This Is A Test"
 */
export const capitalize = (text: string) =>
  text.replace(/\b\w/g, t => t.toUpperCase());

/**
 * This function will split camelCase or PascalCase strings into readable spaced text.
 * @param value string
 * @returns string
 * @example This function will transform PascalCase "AnonymizedManuscript"  to "Anonymized Manuscript"
 */
export const splitPascalCase = (value: string) =>
  value.replace(/([A-Z])/g, ' $1').trim(); // Adds space before capital letters

/**
 * This function will convert camelCase string to Capitalized Case string.
 * @param text string
 * @returns string
 * @example convert "scopeCheck" to "Scope" or "fundingInformation" to "Funding Information"
 */
export const normalizeText = (text: string) => {
  return splitPascalCase(text)
    .replace(/^./, str => str.toUpperCase()) // Capitalizes first letter of sentence
    .trim();
};

/**
 * Convert fieldName to Capitalized Case and will remove "Check" word from input string.
 * @param key string
 * @returns string
 * @example This function will transform "scopeCheck" or "caseData.scopeCheck"  to "Scope".
 */
export const getCheckListName = (key: string) => {
  // Example:  key = "caseData.scopeCheck" or key = "fundingInformation"
  let fieldName = key.split('.').pop() || ''; // scopeCheck
  fieldName = fieldName.replace('Check', ''); // remove Check word from string
  // convert "scopeCheck" to "Scope" or "fundingInformation" to "Funding Information"
  return toSentenceCasePreserveAcronyms(fieldName);
};

/**
 * Pluralizes a string based on the given count.
 *
 * @param {string} word - The word to pluralize.
 * @param {number} count - The quantity to determine pluralization.
 * @param {string} pluralForm - Optional custom plural form.
 * @returns {string} The pluralized string.
 *
 * @example
 * pluralizeString('apple', 3); // returns 'apples'
 * pluralizeString('apple', 0); // returns 'apple'
 * pluralizeString('child', 2, 'children'); // returns 'children'
 */
export const pluralizeString = (
  word: string,
  count: number,
  pluralForm?: string
) => {
  if (count <= 1) return word; // handles 0, 1 and negative values
  if (pluralForm) return pluralForm;

  return word + 's';
};

/**
 * Renders a field name for the count it is quoted with in a validation message.
 *
 * Field names come from the article type configuration and are usually written
 * in the plural (`Figures`, `Tables`), so the singular is needed after a count
 * of one. Only a plural `s`/`ies` ending is swapped, and only a missing `s` is
 * added, so names that are already singular (`Graphical Abstract`) or irregular
 * (`Analysis`) are left alone rather than mangled.
 *
 * @param {string} fieldName - The configured field name.
 * @param {number} count - The quantity the field name is quoted with.
 * @returns {string} The field name in the matching form.
 *
 * @example
 * pluralizeFieldName('Figures', 1); // returns 'Figure'
 * pluralizeFieldName('Figures', 3); // returns 'Figures'
 * pluralizeFieldName('Figure', 3); // returns 'Figures'
 */
export const pluralizeFieldName = (fieldName: string, count: number) => {
  if (count !== 1) {
    return fieldName.endsWith('s') ? fieldName : `${fieldName}s`;
  }

  // Singular names that merely end in "s" (Status, Analysis) are left alone.
  if (/(?:ss|is|us)$/.test(fieldName)) return fieldName;
  if (fieldName.endsWith('ies')) return `${fieldName.slice(0, -3)}y`;
  if (fieldName.endsWith('s')) return fieldName.slice(0, -1);

  return fieldName;
};

/**
 * Converts a string (including camelCase or spaced text) to sentence case.
 * Keeps the first word capitalized and lowercases the rest.
 *
 * @param {string} value - The input string to convert.
 * @returns {string} The sentence-cased string.
 *
 * @example
 * toSentenceCase('English Title');      // returns 'English title'
 * toSentenceCase('englishKeywords');    // returns 'English keywords'
 * toSentenceCase('EnglishKeywords');    // returns 'English keywords'
 * toSentenceCase('english   KEYWORDS'); // returns 'English keywords'
 */
export const toSentenceCase = (value: string): string => {
  if (!value) return value;

  // Convert camelCase / PascalCase to space-separated words
  const withSpaces = value.replace(/([a-z])([A-Z])/g, '$1 $2');

  return withSpaces
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
};

/**
 * Converts a string with abberivations (including camelCase or spaced text) to sentence case.
 * Keeps the first word capitalized and lowercases the rest.
 *
 * @param {string} value - The input string to convert.
 * @returns {string} The sentence-cased string.
 *
 * @example
 * toSentenceCasePreserveAcronyms('manuscript ID');      // returns 'Manuscript ID'
 * toSentenceCasePreserveAcronyms('englishKeywords');    // returns 'English keywords'
 * toSentenceCasePreserveAcronyms('EnglishKeywords');    // returns 'English keywords'
 * toSentenceCasePreserveAcronyms('english   KEYWORDS'); // returns 'English keywords'
 */
export const toSentenceCasePreserveAcronyms = (
  text: string,
  acronyms = ['ID', 'DOI', 'URL', 'ICMJE', 'AI']
) => {
  let result = toSentenceCase(text);

  acronyms.forEach(acronym => {
    const regex = new RegExp(`\\b${acronym}\\b`, 'gi');
    result = result.replace(regex, acronym);
  });

  return result;
};

export const trimLeadingEmptyLines = (content: string): string => {
  const withoutLeadingEmptyBlocks = content.replace(
    /^(?:\s|&nbsp;|<br\s*\/?>|<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>|<div[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>)+/gi,
    ''
  );

  return withoutLeadingEmptyBlocks.replace(
    /^(<(?:p|div)\b[^>]*>)(?:\s|&nbsp;|<br\s*\/?>)+/i,
    '$1'
  );
};

export default {
  capitalize,
  splitPascalCase,
  getCheckListName,
  normalizeText,
  toSentenceCase,
  toSentenceCasePreserveAcronyms,
  trimLeadingEmptyLines
};
